/**
 * The one value that the mounted components of a scene share, such as the reflection probes or the
 * decals of that scene. The first holder makes it, the last one to let go disposes it.
 */
/**
 * Keeps one `Value` for each scene that has a holder.
 *
 * @canonical A value that a scene's mounted components share
 */
export class SceneHolding {
    make;
    /** Written only by `hold` and its release. A scene's entry leaves with its last holder. */
    held = new WeakMap();
    constructor(make) {
        this.make = make;
    }
    /** Holds the value of `scene` for `holder`, and answers the call that lets go. */
    hold(scene, renderer, holder) {
        const held = this.held.get(scene) ?? { value: this.make(scene, renderer), holders: new Set() };
        this.held.set(scene, held);
        held.holders.add(holder);
        return () => {
            held.holders.delete(holder);
            if (held.holders.size > 0)
                return;
            held.value.dispose();
            this.held.delete(scene);
        };
    }
    /** The value of `scene`, or undefined while nothing holds it. */
    of(scene) {
        return this.held.get(scene)?.value;
    }
    /** The value of `scene` for `holder` only when it is the first that still holds it, so it alone does the per-frame work. */
    drivenBy(scene, holder) {
        const held = this.held.get(scene);
        return held?.holders.values().next().value === holder ? held.value : undefined;
    }
}
//# sourceMappingURL=sceneHolding.js.map